<template>
  <div
    ref="wrapperRef"
    class="base-scrollbar"
    :class="$attrs.class"
    :style="wrapperStyles"
  >
    <div
      ref="scrollElRef"
      class="base-scrollbar-scroll-el base-scrollbar-content"
      :style="scrollElCombinedStyle"
      @scroll="onScroll"
      @wheel="onWheel"
    >
      <slot />
    </div>
    <!-- Vertical track + thumb -->
    <template v-if="vertical && showVerticalBar">
      <div
        ref="verticalTrackRef"
        class="base-scrollbar-rail base-scrollbar-rail-y"
        :class="{ 'base-scrollbar-rail-y--above-horizontal': showHorizontalRail }"
        @click="onVerticalTrackClick"
      >
        <div
          ref="verticalThumbRef"
          class="base-scrollbar-thumb base-scrollbar-thumb-y"
          :style="verticalThumbStyle"
          @mousedown="onVerticalThumbMouseDown"
        />
      </div>
    </template>
    <!-- Horizontal track + thumb -->
    <template v-if="showHorizontalRail">
      <div
        ref="horizontalTrackRef"
        class="base-scrollbar-rail base-scrollbar-rail-x"
        :class="{ 'base-scrollbar-rail-x--with-vertical': showVerticalBar }"
        @click="onHorizontalTrackClick"
      >
        <div
          ref="horizontalThumbRef"
          class="base-scrollbar-thumb base-scrollbar-thumb-x"
          :style="horizontalThumbStyle"
          @mousedown="onHorizontalThumbMouseDown"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

interface ScrollbarOptions {
  wheelSpeed?: number
  wheelPropagation?: boolean
  suppressScrollX?: boolean
  suppressScrollY?: boolean
  /** Passed through to underlying scrollbar implementation when supported. */
  swipeEasing?: boolean | string
  /** Passed through to underlying scrollbar implementation when supported. */
  swipePropagation?: boolean
}

interface Props {
  tag?: string
  options?: ScrollbarOptions
  style?: string | Record<string, unknown>
  height?: string
  maxHeight?: string
  horizontal?: boolean
  vertical?: boolean
  updateKey?: string | number
  /** When true, pad the scroll viewport so rails do not cover content (matches rail width/height). */
  reserveRailSpace?: boolean
  /** When true, horizontal scrolling stays enabled but the bottom rail is not rendered (e.g. StickyHorizontalScrollBar). */
  hideHorizontalRail?: boolean
}

/** Must match `.base-scrollbar-rail-y` width and `.base-scrollbar-rail-x` height */
const RAIL_SIZE_PX = 12

const props = withDefaults(defineProps<Props>(), {
  tag: 'div',
  horizontal: true,
  vertical: true,
  updateKey: 0,
  reserveRailSpace: true,
  hideHorizontalRail: false,
  options: () => ({})
})

const emit = defineEmits<{
  'scroll-y': [event: Event]
  'scroll-x': [event: Event]
}>()

const wrapperRef = ref<HTMLElement | null>(null)
const scrollElRef = ref<HTMLElement | null>(null)

const verticalTrackRef = ref<HTMLElement | null>(null)
const verticalThumbRef = ref<HTMLElement | null>(null)
const horizontalTrackRef = ref<HTMLElement | null>(null)
const horizontalThumbRef = ref<HTMLElement | null>(null)

const scrollTop = ref(0)
const scrollLeft = ref(0)
const scrollHeight = ref(0)
const clientHeight = ref(0)
const scrollWidth = ref(0)
const clientWidth = ref(0)

const wrapperStyles = computed(() => {
  const styles: Record<string, string> = {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: '0',
    overflow: 'hidden',
    '--base-scrollbar-rail-size': `${RAIL_SIZE_PX}px`
  }
  if (props.height) styles.height = props.height
  if (props.maxHeight) {
    styles.maxHeight = props.maxHeight
    styles['--base-scrollbar-max-height'] = props.maxHeight
  }
  if (props.style && typeof props.style === 'object') {
    Object.assign(styles, props.style)
  }
  return styles
})

/** Minimum overflow (px) before showing bar - avoids bar for subpixel/rounding and "scroll by itself" when content fits */
const SCROLL_THRESHOLD_PX = 4

const showVerticalBar = computed(
  () => props.vertical && scrollHeight.value - clientHeight.value > SCROLL_THRESHOLD_PX
)
/** Horizontal overflow: scroll axis may be active even when the bottom rail is hidden. */
const hasHorizontalOverflow = computed(
  () => props.horizontal && scrollWidth.value - clientWidth.value > SCROLL_THRESHOLD_PX
)
const showHorizontalRail = computed(
  () => hasHorizontalOverflow.value && !props.hideHorizontalRail
)

/**
 * Keep overflow auto on enabled axes so scrollWidth/scrollHeight reflect full content before the
 * custom rail appears. Tying overflow to showVerticalBar/showHorizontalBar caused a deadlock:
 * overflow hidden → scrollHeight === clientHeight → bar never shows (e.g. OTS per-day table).
 * Native scrollbars stay hidden via scoped CSS (scrollbar-width / webkit).
 */
const scrollElOverflowStyle = computed((): { overflowX: 'auto' | 'hidden'; overflowY: 'auto' | 'hidden' } => ({
  overflowX: props.horizontal ? 'auto' : 'hidden',
  overflowY: props.vertical ? 'auto' : 'hidden'
}))

const scrollElGutterStyle = computed((): Record<string, string> => {
  if (!props.reserveRailSpace) return {}
  const s: Record<string, string> = {}
  /**
   * Always reserve vertical rail width when the vertical axis is enabled — not only when
   * `showVerticalBar` is true. Tying this padding to the overflow flag alone caused reflow
   * and scroll metrics to oscillate at the scroll threshold (flicker / measure loops).
   */
  if (props.vertical) {
    s.paddingRight = `calc(var(--base-scrollbar-inline-end-padding, 0px) + ${RAIL_SIZE_PX}px)`
  }
  if (showHorizontalRail.value) {
    s.paddingBottom = `calc(var(--base-scrollbar-block-end-padding, 0px) + ${RAIL_SIZE_PX}px)`
  }
  return s
})

const scrollElCombinedStyle = computed(() => ({
  ...scrollElOverflowStyle.value,
  ...scrollElGutterStyle.value
}))

const maxScrollTop = computed(() => Math.max(0, scrollHeight.value - clientHeight.value))
const maxScrollLeft = computed(() => Math.max(0, scrollWidth.value - clientWidth.value))

const verticalThumbStyle = computed(() => {
  const sh = scrollHeight.value
  const ch = clientHeight.value
  const max = maxScrollTop.value
  if (sh <= 0 || max <= 0) return { height: '0', top: '0' }
  const ratio = ch / sh
  const heightPct = Math.min(100, ratio * 100)
  const trackRange = 100 - heightPct
  const topPct = max > 0
    ? Math.max(0, Math.min(trackRange, (scrollTop.value / max) * trackRange))
    : 0
  return {
    height: `${heightPct}%`,
    top: `${topPct}%`
  }
})

const horizontalThumbStyle = computed(() => {
  const sw = scrollWidth.value
  const cw = clientWidth.value
  const max = maxScrollLeft.value
  if (sw <= 0 || max <= 0) return { width: '0', left: '0' }
  const ratio = cw / sw
  const widthPct = Math.min(100, ratio * 100)
  const trackRange = 100 - widthPct
  const leftPct = max > 0
    ? Math.max(0, Math.min(trackRange, (scrollLeft.value / max) * trackRange))
    : 0
  return {
    width: `${widthPct}%`,
    left: `${leftPct}%`
  }
})

function measure() {
  const el = scrollElRef.value
  if (!el) return
  scrollHeight.value = el.scrollHeight
  clientHeight.value = el.clientHeight
  scrollWidth.value = el.scrollWidth
  clientWidth.value = el.clientWidth
  const maxT = Math.max(0, scrollHeight.value - clientHeight.value)
  const maxL = Math.max(0, scrollWidth.value - clientWidth.value)
  scrollTop.value = Math.max(0, Math.min(maxT, el.scrollTop))
  scrollLeft.value = Math.max(0, Math.min(maxL, el.scrollLeft))
}

function onScroll() {
  const el = scrollElRef.value
  if (!el) return
  // Use cached dimensions to avoid forcing layout in scroll handler (keeps scroll/wheel responsive)
  const maxT = Math.max(0, scrollHeight.value - clientHeight.value)
  const maxL = Math.max(0, scrollWidth.value - clientWidth.value)
  scrollTop.value = Math.max(0, Math.min(maxT, el.scrollTop))
  scrollLeft.value = Math.max(0, Math.min(maxL, el.scrollLeft))
  scheduleScrollSync()
}

function onWheel(e: WheelEvent) {
  if (!e.shiftKey) return
  const el = scrollElRef.value
  if (!el || maxScrollLeft.value <= 0) return
  e.preventDefault()
  const speed = props.options?.wheelSpeed ?? 1
  // Match useStickyHorizontalScroll + BaseTable: shift+vertical wheel maps to horizontal via -deltaY;
  // prefer deltaX when present (trackpad horizontal gesture).
  const deltaH = e.deltaX !== 0 ? e.deltaX : -e.deltaY
  setScrollLeft(scrollLeft.value + deltaH * speed)
}

function setScrollTop(value: number) {
  const el = scrollElRef.value
  if (!el) return
  const realMax = Math.max(0, el.scrollHeight - el.clientHeight)
  const clamped = Math.max(0, Math.min(realMax, value))
  el.scrollTop = clamped
  scrollTop.value = el.scrollTop
  if (scrollHeight.value !== el.scrollHeight || clientHeight.value !== el.clientHeight) {
    scrollHeight.value = el.scrollHeight
    clientHeight.value = el.clientHeight
  }
}

function setScrollLeft(value: number) {
  const el = scrollElRef.value
  if (!el) return
  const realMax = Math.max(0, el.scrollWidth - el.clientWidth)
  const clamped = Math.max(0, Math.min(realMax, value))
  el.scrollLeft = clamped
  scrollLeft.value = el.scrollLeft
  if (scrollWidth.value !== el.scrollWidth || clientWidth.value !== el.clientWidth) {
    scrollWidth.value = el.scrollWidth
    clientWidth.value = el.clientWidth
  }
}

function onVerticalTrackClick(e: MouseEvent) {
  const track = verticalTrackRef.value
  const el = scrollElRef.value
  if (!track || !el) return
  const realMax = Math.max(0, el.scrollHeight - el.clientHeight)
  if (realMax <= 0) return
  const rect = track.getBoundingClientRect()
  const y = e.clientY - rect.top
  const trackHeight = rect.height
  const clickRatio = Math.max(0, Math.min(1, y / trackHeight))
  setScrollTop(clickRatio * realMax)
}

function onHorizontalTrackClick(e: MouseEvent) {
  const track = horizontalTrackRef.value
  const el = scrollElRef.value
  if (!track || !el) return
  const realMax = Math.max(0, el.scrollWidth - el.clientWidth)
  if (realMax <= 0) return
  const rect = track.getBoundingClientRect()
  const x = e.clientX - rect.left
  const trackWidth = rect.width
  const clickRatio = Math.max(0, Math.min(1, x / trackWidth))
  setScrollLeft(clickRatio * realMax)
}

let verticalDragging = false
let verticalDragOffsetY = 0

function onVerticalThumbMouseDown(e: MouseEvent) {
  e.preventDefault()
  const thumb = verticalThumbRef.value
  if (!thumb) return
  verticalDragging = true
  const thumbRect = thumb.getBoundingClientRect()
  verticalDragOffsetY = e.clientY - thumbRect.top
  document.addEventListener('mousemove', onVerticalDocMouseMove)
  document.addEventListener('mouseup', onVerticalDocMouseUp)
}

function onVerticalDocMouseMove(e: MouseEvent) {
  if (!verticalDragging) return
  const track = verticalTrackRef.value
  const thumb = verticalThumbRef.value
  const el = scrollElRef.value
  if (!track || !thumb || !el) return
  const realMax = Math.max(0, el.scrollHeight - el.clientHeight)
  if (realMax <= 0) return
  const trackRect = track.getBoundingClientRect()
  const thumbHeight = thumb.getBoundingClientRect().height
  const trackHeight = trackRect.height
  const thumbRange = Math.max(0, trackHeight - thumbHeight)
  let thumbTopInTrack = e.clientY - trackRect.top - verticalDragOffsetY
  thumbTopInTrack = Math.max(0, Math.min(thumbRange, thumbTopInTrack))
  const scrollRatio = thumbRange > 0 ? thumbTopInTrack / thumbRange : 0
  setScrollTop(scrollRatio * realMax)
}

function onVerticalDocMouseUp() {
  verticalDragging = false
  document.removeEventListener('mousemove', onVerticalDocMouseMove)
  document.removeEventListener('mouseup', onVerticalDocMouseUp)
}

let horizontalDragging = false
let horizontalDragOffsetX = 0

function onHorizontalThumbMouseDown(e: MouseEvent) {
  e.preventDefault()
  const thumb = horizontalThumbRef.value
  if (!thumb) return
  horizontalDragging = true
  const thumbRect = thumb.getBoundingClientRect()
  horizontalDragOffsetX = e.clientX - thumbRect.left
  document.addEventListener('mousemove', onHorizontalDocMouseMove)
  document.addEventListener('mouseup', onHorizontalDocMouseUp)
}

function onHorizontalDocMouseMove(e: MouseEvent) {
  if (!horizontalDragging) return
  const track = horizontalTrackRef.value
  const thumb = horizontalThumbRef.value
  const el = scrollElRef.value
  if (!track || !thumb || !el) return
  const realMax = Math.max(0, el.scrollWidth - el.clientWidth)
  if (realMax <= 0) return
  const trackRect = track.getBoundingClientRect()
  const thumbWidth = thumb.getBoundingClientRect().width
  const trackWidth = trackRect.width
  const thumbRange = Math.max(0, trackWidth - thumbWidth)
  let thumbLeftInTrack = e.clientX - trackRect.left - horizontalDragOffsetX
  thumbLeftInTrack = Math.max(0, Math.min(thumbRange, thumbLeftInTrack))
  const scrollRatio = thumbRange > 0 ? thumbLeftInTrack / thumbRange : 0
  setScrollLeft(scrollRatio * realMax)
}

function onHorizontalDocMouseUp() {
  horizontalDragging = false
  document.removeEventListener('mousemove', onHorizontalDocMouseMove)
  document.removeEventListener('mouseup', onHorizontalDocMouseUp)
}

const forceUpdate = async () => {
  await nextTick()
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      measure()
    })
  })
}

function getScrollElement(): HTMLElement | null {
  return scrollElRef.value
}

let resizeObserver: ResizeObserver | null = null
let pendingMeasureRaf = 0
let pendingScrollSyncRaf = 0

function scheduleMeasure() {
  if (pendingMeasureRaf) return
  pendingMeasureRaf = requestAnimationFrame(() => {
    pendingMeasureRaf = 0
    measure()
  })
}

function scheduleScrollSync() {
  if (pendingScrollSyncRaf) return
  pendingScrollSyncRaf = requestAnimationFrame(() => {
    pendingScrollSyncRaf = 0
    const el2 = scrollElRef.value
    if (!el2) return
    if (scrollHeight.value !== el2.scrollHeight || clientHeight.value !== el2.clientHeight) {
      scrollHeight.value = el2.scrollHeight
      clientHeight.value = el2.clientHeight
    }
    if (scrollWidth.value !== el2.scrollWidth || clientWidth.value !== el2.clientWidth) {
      scrollWidth.value = el2.scrollWidth
      clientWidth.value = el2.clientWidth
    }
    emit('scroll-y', new Event('scroll'))
    emit('scroll-x', new Event('scroll'))
  })
}

onMounted(async () => {
  await nextTick()
  measure()
  const el = scrollElRef.value
  if (el && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      scheduleMeasure()
    })
    resizeObserver.observe(el)
    // When content grows (e.g. list loads), re-measure so thumb size is correct before user scrolls
    const content = el.firstElementChild
    if (content && content instanceof HTMLElement) {
      resizeObserver.observe(content)
    }
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      measure()
    })
  })
})

watch(() => props.updateKey, () => {
  forceUpdate()
}, { flush: 'post' })

/** Padding/gutter toggles change client metrics; re-measure so thumbs and overflow flags stay correct */
watch(
  [showVerticalBar, showHorizontalRail, () => props.reserveRailSpace],
  () => {
    nextTick(() => scheduleMeasure())
  },
  { flush: 'post' }
)

onBeforeUnmount(() => {
  if (pendingMeasureRaf) cancelAnimationFrame(pendingMeasureRaf)
  pendingMeasureRaf = 0
  if (pendingScrollSyncRaf) cancelAnimationFrame(pendingScrollSyncRaf)
  pendingScrollSyncRaf = 0
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  resizeObserver = null
  document.removeEventListener('mousemove', onVerticalDocMouseMove)
  document.removeEventListener('mouseup', onVerticalDocMouseUp)
  document.removeEventListener('mousemove', onHorizontalDocMouseMove)
  document.removeEventListener('mouseup', onHorizontalDocMouseUp)
})

defineExpose({
  forceUpdate,
  getScrollElement,
  get ps() {
    return scrollElRef.value
  }
})
</script>

<style scoped>
.base-scrollbar {
  box-sizing: border-box;
}

.base-scrollbar-scroll-el {
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: var(--base-scrollbar-max-height, none);
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.base-scrollbar-scroll-el::-webkit-scrollbar {
  display: none;
}

/* Without this, the sole flex item shrinks to the scrollport max-height and scrollHeight never exceeds clientHeight, so the custom rail never appears (e.g. Per day table under max-height). */
.base-scrollbar-scroll-el > * {
  flex-shrink: 0;
}

.base-scrollbar-content > *:last-child {
  margin-bottom: 0 !important;
}

.base-scrollbar-content [class*="space-y"] > *:last-child {
  margin-bottom: 0 !important;
}

/* Vertical rail - right side */
.base-scrollbar-rail-y {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 12px;
  padding: 4px 0;
  border-radius: 6px;
  background: var(--color-surface-hover);
  cursor: pointer;
  z-index: 20;
}

.base-scrollbar-rail-y--above-horizontal {
  bottom: var(--base-scrollbar-rail-size, 12px);
}

.base-scrollbar-thumb-y {
  position: absolute;
  left: 2px;
  width: 8px;
  min-height: 24px;
  border-radius: 4px;
  background: var(--color-text-muted);
  cursor: grab;
  transition: background 0.15s ease;
}

.base-scrollbar-thumb-y:active {
  cursor: grabbing;
}

/* Horizontal rail - bottom */
.base-scrollbar-rail-x {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 12px;
  padding: 0 4px;
  border-radius: 6px;
  background: var(--color-surface-hover);
  cursor: pointer;
}

.base-scrollbar-rail-x--with-vertical {
  right: 12px;
}

.base-scrollbar-thumb-x {
  position: absolute;
  top: 2px;
  height: 8px;
  min-width: 24px;
  border-radius: 4px;
  background: var(--color-text-muted);
  cursor: grab;
  transition: background 0.15s ease;
}


.base-scrollbar-thumb-x:active {
  cursor: grabbing;
}
</style>
